import { sanitizePublicPayload } from './contracts.js'

export const toMaskedTicket = (value) => {
  const text = String(value ?? '').replace(/\D/g, '')
  if (!text) return 'Unknown ticket'
  if (text.length <= 2) return `#${text}`
  return `#${'*'.repeat(Math.max(0, text.length - 2))}${text.slice(-2)}`
}

export const toPublicWinnerLabel = (winner, index = 0) => {
  const display = String(winner?.display_name || winner?.public_name || '').trim()
  if (display) return display
  return `Winner ${index + 1}`
}

export const isPublicResultVisible = (lotteryStatus) => {
  const normalized = String(lotteryStatus || '').trim().toLowerCase()
  return normalized === 'drawn' || normalized === 'archived'
}

export const toPublicStatusLabel = (lotteryStatus) => {
  const normalized = String(lotteryStatus || '').trim().toLowerCase()
  if (normalized === 'drawn') return 'Draw completed'
  if (normalized === 'archived') return 'Draw archived'
  if (normalized === 'closed') return 'Draw pending'
  if (normalized === 'active') return 'Ticket sales active'
  return 'Status unavailable'
}

export const toPublicPaymentStatusLabel = (paymentStatus) => {
  const normalized = String(paymentStatus || '').trim().toLowerCase().replace(/\s+/g, '_')
  if (normalized === 'paid') return 'Payment confirmed'
  if (normalized === 'authorized') return 'Payment approved'
  if (normalized === 'pending') return 'Payment pending'
  if (normalized === 'failed') return 'Payment could not be confirmed'
  if (normalized === 'refunded' || normalized === 'partially_refunded') return 'Payment refunded'
  if (normalized === 'expired') return 'Payment expired'
  if (normalized === 'cancelled') return 'Payment cancelled'
  return 'Payment status unavailable'
}

export const toPublicPaymentMessage = (paymentStatus) => {
  const normalized = String(paymentStatus || '').trim().toLowerCase().replace(/\s+/g, '_')
  if (normalized === 'paid' || normalized === 'authorized') return 'Your ticket payment is registered.'
  if (normalized === 'pending') return 'Your payment is still being confirmed.'
  if (normalized === 'failed') return 'We could not confirm payment yet. Please retry later or contact support.'
  if (normalized === 'refunded' || normalized === 'partially_refunded') return 'This ticket payment has been refunded.'
  if (normalized === 'expired' || normalized === 'cancelled') return 'This payment session is no longer active.'
  return ''
}

export const toPublicErrorMessage = (err, fallback = 'Could not load public lottery data.') => {
  const status = Number(err?.response?.status || 0)
  const detail = String(err?.response?.data?.detail || '').toLowerCase()
  if (status === 404 || detail.includes('not found')) return 'We could not find that lottery or ticket reference.'
  if (status === 410 || detail.includes('archived')) return 'This lottery is archived and no longer available for lookup updates.'
  if (status === 409 || detail.includes('conflict')) return 'The result is being finalized. Please try again shortly.'
  if (status === 504 || status === 408 || detail.includes('timeout')) return 'The service is temporarily slow. Please retry in a moment.'
  return fallback
}

export const toPublicSupportReference = (err) => String(
  err?.response?.headers?.['x-correlation-id']
  || err?.response?.headers?.['x-request-id']
  || '',
)

export const sanitizePublicDrawPayload = (payload) => {
  const source = sanitizePublicPayload(payload && typeof payload === 'object' ? payload : {})
  const winners = Array.isArray(source?.winners) ? source.winners : []
  return {
    lotteryName: source?.lottery_name || source?.name || 'Lottery',
    status: String(source?.status || '').trim().toLowerCase(),
    drawTimestamp: source?.draw_timestamp || source?.executed_at || null,
    prizeOverview: source?.prize_overview || source?.prizes_summary || 'Prize information unavailable.',
    paymentStatusLabel: toPublicPaymentStatusLabel(source?.payment_status ?? source?.payment_summary?.status),
    paymentMessage: toPublicPaymentMessage(source?.payment_status ?? source?.payment_summary?.status),
    integrity: {
      totalTickets: Number(source?.integrity?.total_tickets ?? source?.total_tickets ?? 0),
      winnersCount: Number(source?.integrity?.winners_count ?? winners.length),
    },
    winners: winners.map((winner, index) => ({
      key: winner?.id ?? `winner-${index}`,
      label: toPublicWinnerLabel(winner, index),
      ticket: toMaskedTicket(winner?.ticket_number ?? winner?.winning_number),
    })),
  }
}

